import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AppThunk, RootState } from '../../app/store'
import { BillingClient, resetSubscription } from '../../pages/Billing/BillingSlice'
import {
  clearAuthenticatedOrganizationContext,
  OrganizationContextClient,
  setAuthenticatedOrganizationContext,
} from '../../pages/OrganizationProfilePage/OrganizationSlice'
import {
  clearCurrentClinic,
  selectClinic,
} from '../../pages/NewScriptPage/ScriptForm/ClinicContextSlice'
import {
  clearOrganizationUsers,
  OrganizationUsersClient,
  setOrganizationUsers,
} from '../users/UsersSlice'
import { setPrescriber } from '../../pages/NewScriptPage/ScriptForm/PrescriberSlice'
import { setOrganizationAdmin } from '../organization-admin/organizationAdminSlice'

export type AuthenticatedContextStatus = 'idle' | 'loading' | 'ready' | 'error'
export type AuthenticatedContextDependency =
  | 'currentOrganization'
  | 'entitlements'
  | 'organizationUsers'
  | 'currentClinic'

interface AuthenticatedContextState {
  status: AuthenticatedContextStatus
  generation: number
  scopeKey?: string
  failedDependency?: AuthenticatedContextDependency
}

export type AuthenticatedContextResult =
  | { status: 'idle' | 'ready' | 'stale' }
  | { status: 'error'; failedDependency: AuthenticatedContextDependency }

interface DependencyFailure {
  dependency: AuthenticatedContextDependency
}

interface InFlightContext {
  scopeKey: string
  promise: Promise<AuthenticatedContextResult>
}

interface ActiveContextScope {
  scopeKey: string
  userId: string
  profileId: string
  profileType: 'prescriber' | 'admin'
}

const initialState: AuthenticatedContextState = {
  status: 'idle',
  generation: 0,
}

const invalidateState = (state: AuthenticatedContextState) => {
  state.status = 'idle'
  state.generation += 1
  state.scopeKey = undefined
  state.failedDependency = undefined
}

let inFlightContext: InFlightContext | undefined

const authenticatedContextSlice = createSlice({
  name: 'authenticatedContext',
  initialState,
  reducers: {
    contextLoadStarted: (
      state,
      { payload }: PayloadAction<{ generation: number; scopeKey: string }>
    ) => {
      state.status = 'loading'
      state.generation = payload.generation
      state.scopeKey = payload.scopeKey
      state.failedDependency = undefined
    },
    contextLoadReady: (
      state,
      { payload }: PayloadAction<{ generation: number; scopeKey: string }>
    ) => {
      if (state.generation !== payload.generation || state.scopeKey !== payload.scopeKey) return
      state.status = 'ready'
      state.failedDependency = undefined
    },
    contextLoadFailed: (
      state,
      {
        payload,
      }: PayloadAction<{
        generation: number
        scopeKey: string
        dependency: AuthenticatedContextDependency
      }>
    ) => {
      if (state.generation !== payload.generation || state.scopeKey !== payload.scopeKey) return
      state.status = 'error'
      state.failedDependency = payload.dependency
    },
    contextInvalidated: (state) => {
      invalidateState(state)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setPrescriber, (state) => invalidateState(state))
      .addCase(setOrganizationAdmin, (state) => invalidateState(state))
  },
})

const { contextLoadStarted, contextLoadReady, contextLoadFailed, contextInvalidated } =
  authenticatedContextSlice.actions

const getActiveContextScope = (state: RootState): ActiveContextScope | undefined => {
  const userId = state.authentication.userId
  const prescriber = state.prescriber.prescriber
  const admin = state.organizationAdmin.profile
  const organizationId = prescriber?.organization?.id ?? admin?.organization?.id
  const profileId = prescriber?.id ?? admin?.id
  const profileType = prescriber ? 'prescriber' : 'admin'

  if (!userId || !organizationId || !profileId) return undefined
  return {
    scopeKey: JSON.stringify([userId, organizationId, profileType, profileId]),
    userId,
    profileId,
    profileType,
  }
}

const usersIncludeActiveProfile = (
  users: Awaited<ReturnType<typeof OrganizationUsersClient.getAllUsersInOrg>>,
  scope: ActiveContextScope
) => {
  const currentUser = users.find((user) => user.userId === scope.userId)
  return scope.profileType === 'prescriber'
    ? currentUser?.prescriber?.id === scope.profileId
    : currentUser?.admin?.id === scope.profileId
}

const loadDependency = async <T>(
  dependency: AuthenticatedContextDependency,
  request: Promise<T>
): Promise<T> => {
  try {
    return await request
  } catch {
    throw { dependency } as DependencyFailure
  }
}

const clearPublishedContext = (dispatch: Parameters<AppThunk>[0]) => {
  dispatch(clearAuthenticatedOrganizationContext())
  dispatch(clearOrganizationUsers())
  dispatch(clearCurrentClinic())
  dispatch(resetSubscription())
}

export const invalidateAuthenticatedContext = (): AppThunk => (dispatch) => {
  dispatch(contextInvalidated())
  clearPublishedContext(dispatch)
}

export const loadAuthenticatedContext =
  (options: { force?: boolean } = {}): AppThunk<Promise<AuthenticatedContextResult>> =>
  (dispatch, getState) => {
    const scope = getActiveContextScope(getState())
    if (!scope) return Promise.resolve({ status: 'idle' })
    const { scopeKey } = scope

    const currentContext = getState().authenticatedContext
    if (
      !options.force &&
      currentContext.status === 'ready' &&
      currentContext.scopeKey === scopeKey
    ) {
      return Promise.resolve({ status: 'ready' })
    }
    if (
      !options.force &&
      currentContext.status === 'loading' &&
      currentContext.scopeKey === scopeKey &&
      inFlightContext?.scopeKey === scopeKey
    ) {
      return inFlightContext.promise
    }

    const generation = currentContext.generation + 1
    dispatch(contextLoadStarted({ generation, scopeKey }))
    clearPublishedContext(dispatch)
    void dispatch(BillingClient.getSubscription())

    const promise = Promise.all([
      loadDependency('currentOrganization', OrganizationContextClient.getCurrentOrganization()),
      loadDependency('entitlements', OrganizationContextClient.getCurrentEntitlements()),
      loadDependency('organizationUsers', OrganizationUsersClient.getAllUsersInOrg()),
    ])
      .then(([organization, entitlements, users]) => {
        if (!organization.clinic) {
          throw { dependency: 'currentClinic' } as DependencyFailure
        }
        if (!usersIncludeActiveProfile(users, scope)) {
          throw { dependency: 'organizationUsers' } as DependencyFailure
        }

        const activeContext = getState().authenticatedContext
        const currentScope = getActiveContextScope(getState())
        if (
          activeContext.generation !== generation ||
          activeContext.scopeKey !== scopeKey ||
          currentScope?.scopeKey !== scopeKey
        ) {
          return { status: 'stale' } as const
        }

        dispatch(setAuthenticatedOrganizationContext({ organization, entitlements }))
        dispatch(setOrganizationUsers(users))
        dispatch(selectClinic(organization.clinic))
        dispatch(contextLoadReady({ generation, scopeKey }))
        return { status: 'ready' } as const
      })
      .catch((failure: DependencyFailure) => {
        const activeContext = getState().authenticatedContext
        if (activeContext.generation !== generation || activeContext.scopeKey !== scopeKey) {
          return { status: 'stale' } as const
        }

        const dependency = failure.dependency ?? 'currentOrganization'
        dispatch(contextLoadFailed({ generation, scopeKey, dependency }))
        return { status: 'error', failedDependency: dependency } as const
      })
      .finally(() => {
        if (inFlightContext?.promise === promise) inFlightContext = undefined
      })

    inFlightContext = { scopeKey, promise }
    return promise
  }

export const retryAuthenticatedContext = (): AppThunk<Promise<AuthenticatedContextResult>> =>
  loadAuthenticatedContext({ force: true })

export default authenticatedContextSlice.reducer
